import React, { useState } from "react";
import api from "@/lib/api";
import { TrashIcon as Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Small inline delete button used on dog list cards and post-litter
 * confirmation rows.
 *
 * Why this exists (Janis-bug 2026-06):
 *   The only Delete button used to live in the right-rail sidebar of
 *   `/dogs/:id`. On mobile, the sidebar renders *below* the entire dog
 *   detail (Overview + 7 tab sections), so deleting a duplicate puppy
 *   required scrolling past everything to find it. Breeders gave up.
 *
 *   This component drops the same `DELETE /api/dogs/:id` action where
 *   the breeder actually NOTICES a duplicate — the Dogs list card and
 *   the post-registration LitterConfirmation row.
 *
 * Behaviour:
 *   • Rendered ON TOP of a parent `<Link>` (or any clickable card).
 *     Stops click propagation so tapping the trash icon doesn't
 *     navigate to the dog detail.
 *   • Confirms via AlertDialog with the dog's name + reg number so
 *     the breeder sees exactly which puppy will be removed.
 *   • Calls onDeleted() so the parent can drop the row from its list
 *     without a full page reload — keeps the user's scroll position.
 */
export default function DogCardDeleteButton({
  dogId,
  dogName,
  registrationNumber = "",
  onDeleted,
  size = "default",
  label,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/dogs/${dogId}`);
      toast.success(`${dogName || "Dog"} removed`);
      setOpen(false);
      onDeleted?.(dogId);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // Stop the parent `<Link>` from firing when the user taps the trash.
  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const compact = size === "compact";

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          stop(e);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            stop(e);
            setOpen(true);
          }
        }}
        data-testid={`dog-card-delete-${dogId}`}
        aria-label={`Delete ${dogName || "dog"}`}
        title={`Delete ${dogName || "dog"}`}
        className={
          compact
            ? "inline-flex items-center justify-center h-8 w-8 rounded-full text-crimson hover:bg-[#C1272D]/10 active:bg-[#C1272D]/20 transition-colors"
            : "inline-flex items-center gap-1.5 text-xs font-semibold text-crimson hover:text-white hover:bg-crimson active:bg-[#9A1F25] border border-[#C1272D]/30 hover:border-crimson rounded-full px-3 py-1.5 transition-colors"
        }
      >
        <Trash size={compact ? 14 : 13} weight="duotone" />
        {!compact && (label || "Delete")}
      </button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent onClick={stop}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {dogName || "this dog"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {registrationNumber ? (
                <>
                  This will permanently remove{" "}
                  <strong className="text-ink">{dogName}</strong> ({" "}
                  <span className="font-mono text-xs">
                    {registrationNumber}
                  </span>{" "}
                  ) from your registry. The certificate PDF will no longer
                  resolve.
                </>
              ) : (
                <>
                  This will permanently remove{" "}
                  <strong className="text-ink">{dogName}</strong> from your
                  registry. This cannot be undone.
                </>
              )}{" "}
              If this dog is listed as a sire or dam on another dog&apos;s
              pedigree, that pedigree branch will become orphaned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={stop}
              data-testid={`dog-card-delete-cancel-${dogId}`}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(e) => {
                stop(e);
                handleDelete();
              }}
              data-testid={`dog-card-delete-confirm-${dogId}`}
              className="bg-crimson hover:bg-[#9A1F25]"
            >
              {busy ? "Deleting…" : "Delete forever"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
